import React, { useState } from 'react';
import styled from 'styled-components/macro';
import {
  Collapse,
  Divider,
  InputBase as MuiInput,
  MenuItem as MuiMenuItem,
  Tooltip,
  Fade,
  Typography
} from '@material-ui/core';
import { TextString } from '../../components/Text';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircleOutline';
import {
  StyledFieldTypeMenuItem,
  FieldTypeIcon
} from '../form-builder/FormBuilderFieldForm';
import OTSelect from '../../components/engine/SelectEngine';
import { compareTypeMap, conditionDisabled } from './compareTypeMap';
import { SmallIconButton } from './SmallIconButton';
import { ComparisonBuilder } from './ComparisonBuilder';
import { getAllFields } from '../form-builder/getAllFields';
import { InlineSelect } from './InlineSelect';
import { defaultComparisonMap } from './defaultComparisonMap';
import { useSelector } from 'react-redux';

import InlineView from './view/ConditionBuilderInlineView';

export const filterConditionalTypes = ({ uiType }) =>
  Object.keys(compareTypeMap).includes(uiType);

const getFieldById = (form, fieldId) =>
  getAllFields(form).find(f => f.internalId === fieldId);

const ConditionWrapper = styled.div`
  padding: 16px;
  background-color: ${({ theme }) => theme.palette.pale_grey};
  transition: opacity 0.15s linear;
`;

const ConditionSubheader = styled.div`
  padding: 16px;
  display: flex;
  align-items: center;

  span {
    flex: 1;
  }
`;

const Select = styled(OTSelect)`
  && {
    background: none;
    height: 4em;
    border: 1px solid #c7c7c7;
  }
`;

export const AndOr = ({ andOr, update }) => (
  <InlineSelect
    value={andOr}
    onChange={e => update(e.target.value)}
    input={<MuiInput />}
    IconComponent={() => null}
    SelectDisplayProps={{ style: { paddingRight: 8, fontWeight: 700 } }}
    data-testid="logicbuilder-select-and-or"
  >
    <MuiMenuItem
      data-testid="logicbuilder-select-and-or-option-and"
      value="and"
    >
      and
    </MuiMenuItem>
    <MuiMenuItem data-testid="logicbuilder-select-and-or-option-or" value="or">
      or
    </MuiMenuItem>
  </InlineSelect>
);

export const ConditionBuilder = ({
  form: passedForm,
  formId,
  internalId,
  condition,
  conditions,
  update,
  i,
  remove,
  preferSelf,
  rootAndOr,
  allowRootAndOrToggle,
  updateRootAndOr,
  variant = 'drawer',
  firstLinePrefix,
  add // used in inline view for adding new condition
}) => {
  const form =
    useSelector(state => state.state.formBuilder[formId]) || passedForm;
  const { comparisons = [], andOr, input } = condition;
  const inputField = getFieldById(form, input);
  const inputType = inputField && inputField.uiType;
  const compareType = inputType && compareTypeMap[inputType];
  const options =
    (inputField && inputField.uiType === 'status' && form.customStatuses) ||
    (inputField && inputField.options) ||
    [];

  // filters the target field from list of fields
  const filterSelf = preferSelf ? () => true : f => f.internalId !== internalId;

  const filterLookupChildren = f => {
    if (
      !f.lookupInfo ||
      !f.lookupInfo.subFieldInfo ||
      !f.lookupInfo.subFieldInfo.parentFieldId ||
      f.lookupInfo.subFieldInfo.parentFieldId !== internalId
    ) {
      return true;
    }
    return false;
  };

  let fields = form.fields;

  if (
    internalId &&
    form &&
    form.adminFields &&
    form.adminFields.filter(one => one.internalId === internalId).length
  ) {
    fields = getAllFields(form);
  }

  if (!internalId && form) {
    // get admin fields for reminders - on the reminders page we don't use internalId
    fields = getAllFields(form);
  }

  const inputFieldChoices = fields
    // only supported logic types
    .filter(filterConditionalTypes)
    // don't show self in dropdown
    .filter(filterSelf)
    // don't show lookup children of self
    .filter(filterLookupChildren)
    // don't show archived fields
    .filter(field => !field.archived)
    .sort((a, b) => {
      return a.position - b.position;
    });

  const [deleteMe, setDeleteMe] = React.useState(false);
  const [removeHovered, setRemovedHovered] = React.useState(false);

  const updateQuestion = id => {
    update({
      input: id,
      andOr: 'or',
      comparisons: [
        defaultComparisonMap[compareTypeMap[getFieldById(form, id).uiType]]
      ]
    });
  };

  const viewProps = {
    add,
    condition,
    inputFieldChoices,
    compareType,
    input,
    updateQuestion,

    removeHovered,
    setRemovedHovered,
    deleteMe,
    setDeleteMe,
    i,

    comparisons,
    update,
    andOr,
    remove,
    options,

    rootAndOr,
    updateRootAndOr,
    variant,
    firstLinePrefix
  };

  return (
    <Collapse
      appear
      in={deleteMe !== condition._id}
      onExited={remove}
      data-testid="logicbuilder-condition"
    >
      {variant === 'drawer' ? (
        <>
          <ConditionSubheader>
            <span>
              {i > 0 &&
                (allowRootAndOrToggle ? (
                  <b>
                    <AndOr andOr={rootAndOr} update={updateRootAndOr} />
                  </b>
                ) : (
                  'and '
                ))}
              <TextString content={'logic.when_the_answer_to'} />
            </span>
            <span style={{ flex: 'none' }}>
              <Fade in={conditions.length > 0 || Boolean(input)}>
                <span>
                  <Tooltip title="Remove condition" placement="left">
                    <span>
                      <SmallIconButton
                        onMouseOver={() => setRemovedHovered(true)}
                        onMouseLeave={() => setRemovedHovered(false)}
                        onClick={() => setDeleteMe(condition._id)}
                      >
                        <RemoveCircleIcon />
                      </SmallIconButton>
                    </span>
                  </Tooltip>
                </span>
              </Fade>
            </span>
          </ConditionSubheader>
          <ConditionWrapper style={{ opacity: removeHovered ? 0.33 : 1 }}>
            <div>
              <Select
                fullWidth
                searchable
                virtualize
                virtualiziztionProps={{ itemSize: 60, width: '100%' }}
                data-testid={`logicbuilder-condition-select-field-${i}`}
                value={(compareType && input) || ''}
                onChange={updateQuestion}
                placeholder="Choose a question"
                options={inputFieldChoices.map(
                  ({ internalId: id, uiType, title, lookupInfo }) => ({
                    value: id,
                    'data-testid': `logicbuilder-condition-select-field-value-${id}`,
                    text: title || `Untitled ${uiType} field`,
                    uiType,
                    disabled: conditionDisabled.includes(uiType),
                    isSubField: lookupInfo && lookupInfo.subFieldInfo
                  })
                )}
                openMenuOptionParser={opt => {
                  return (
                    <>
                      <FieldTypeIcon fieldType={opt.uiType} />
                      <span
                        style={{
                          maxWidth: 400,
                          display: 'inline-block',
                          whiteSpace: 'normal',
                          width: 'calc(100% - 64px)'
                        }}
                      >
                        <Typography>
                          <TextString content={opt.text} />
                        </Typography>
                      </span>
                    </>
                  );
                }}
              />
            </div>
            {comparisons.map(
              ({ _id }, j) =>
                compareType && (
                  <ComparisonBuilder
                    {...{
                      comparisons,
                      input,
                      compareType,
                      j,
                      update,
                      andOr,
                      remove,
                      options
                    }}
                    key={_id || j}
                  />
                )
            )}
          </ConditionWrapper>
          <Divider />{' '}
        </>
      ) : (
        <InlineView {...viewProps} />
      )}
    </Collapse>
  );
};
